import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { Box, Typography, Alert, Grid, Card, useTheme, useMediaQuery } from "@mui/material";
import { LocaleContext } from "../../routing/LangRouter";
import { useTranslation } from "react-i18next";

//import constants
import BASE_URL from "../../constants/baseUrls";

//import components
import { TicketPreview } from "../../components/view/bus/ticket-preview";
import { SeatMap, getAdjacentGenderConflict } from "../../components/view/bus/seat-map";
import { PassengerFormFields } from "../../components/view/bus/passenger-form-fields";
import { BaseButton } from "../../components/common/base-button/base-button";
import Loading from "../../components/common/loading/loading";
import MobileHeader from "../../components/common/mobile-header/mobile-header";

const emptyPassenger = () => ({
    seatNumber: null,
    first_name: "",
    last_name: "",
    gender: "",
    national_code: "",
    birthdate: "",
    phone_number: "",
});

const MAX_SEATS = 10;
export default function PassengerInfoPage() {
    const navigate = useNavigate();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();

    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down("sm"));

    const accessToken = localStorage.getItem("accessToken");
    const selectedBus = JSON.parse(sessionStorage.getItem("selectedBus"));
    const busPassengers = JSON.parse(sessionStorage.getItem("busPassengers"));
    const uidData = JSON.parse(sessionStorage.getItem("uidData"));

    const navigateToBusPage = () => {
        sessionStorage.removeItem("uidData");
        sessionStorage.removeItem("selectedBus");
        sessionStorage.removeItem("busPassengers");
        navigate(`/${locale}/bus`);
    };

    const [busItem, setBusItem] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const getBusItem = async () => {
        try {
            setIsLoading(true);
            const response = await axios.post(
                `${BASE_URL}/api/v2/reserve/bus/detail/`,
                {
                    uid: uidData.uid,
                    source_code: selectedBus.source_code,
                    bus_code: selectedBus.bus_code,
                },
                {
                    headers: {
                        Authorization: accessToken ? `Bearer ${accessToken}` : "",
                    },
                },
            );
            setBusItem(response?.data?.data);
        } catch (error) {
            navigateToBusPage();
        } finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        getBusItem();
    }, []);

    // const [selectedSeats, setSelectedSeats] = useState([]);
    const [selectedSeats, setSelectedSeats] = useState(() => (busPassengers?.passengers ?? []).map(p => p.seatNumber).filter(Boolean));
    const [seatError, setSeatError] = useState("");

    const { control, watch, handleSubmit, setValue, getValues, formState } = useForm({
        defaultValues: {
            passengers: busPassengers?.passengers?.length ? busPassengers.passengers : [emptyPassenger()],
        },
        mode: "onTouched",
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "passengers",
    });

    const handleToggleSeat = (seatNumber, isSelecting) => {
        setSeatError("");

        if (isSelecting) {
            if (selectedSeats.length >= MAX_SEATS) return;

            setSelectedSeats(prev => [...prev, seatNumber]);

            const passengers = getValues("passengers");
            const emptyIndex = passengers.findIndex(p => !p.seatNumber);

            if (emptyIndex !== -1) {
                setValue(`passengers.${emptyIndex}.seatNumber`, seatNumber, { shouldValidate: false });
            } else {
                append({ ...emptyPassenger(), seatNumber }, { shouldFocus: false });
            }
        } else {
            setSelectedSeats(prev => prev.filter(n => n !== seatNumber));

            const passengers = getValues("passengers");
            const idx = passengers.findIndex(p => p.seatNumber === seatNumber);

            if (idx === -1) return;

            if (passengers.length === 1) {
                setValue(`passengers.${idx}.seatNumber`, null, { shouldValidate: false });
            } else {
                remove(idx);
            }
        }
    };

    const totalRequiredSeats = fields.length;
    const allSeatsAssigned = selectedSeats.length === totalRequiredSeats && totalRequiredSeats > 0;

    const onSubmit = data => {
        if (!allSeatsAssigned) {
            setSeatError(
                `لطفاً به تعداد مسافران (${totalRequiredSeats} نفر) صندلی انتخاب کنید. در حال حاضر ${selectedSeats.length} صندلی انتخاب شده است.`,
            );
            return;
        }

        for (const p of data.passengers) {
            const seat = busItem?.seats?.find(s => s.number === p.seatNumber);
            const conflict = getAdjacentGenderConflict(busItem?.seats, seat, p.gender);

            if (conflict) {
                setSeatError(`صندلی ${p.seatNumber} کنار مسافر ${conflict === "2" ? "خانم" : "آقا"} قرار دارد. لطفاً اصلاح کنید.`);
                return;
            }
        }

        setSeatError("");
        sessionStorage.setItem("busPassengers", JSON.stringify(data));
        navigate(`/${locale}/confirm-information`);
    };

    if (isLoading) return <Loading />;

    return (
        <Box
            sx={{ maxWidth: 900, mx: "auto", px: { xs: 1, sm: 2 }, py: 3, rowGap: 2, display: "flex", flexDirection: "column", mt: { xs: 5, md: 0 } }}
        >
            <MobileHeader title={t("passengersInformation")} justifyContent="center" handleClick={navigateToBusPage} />
            <TicketPreview ticket={busItem} onEditTicket={navigateToBusPage} />

            <SeatMap seats={busItem?.seats} selectedSeats={selectedSeats} onToggleSeat={handleToggleSeat} requiredCount={totalRequiredSeats} />

            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                    اطلاعات مسافران
                </Typography>

                {fields?.map((field, index) => (
                    <PassengerFormFields key={field.id} control={control} index={index} isFirst={index === 0} />
                ))}
            </Box>
            {!!busItem?.refundRules?.length && (
                <Card variant="outlined" sx={{ p: { xs: 1.5, sm: 2.5, md: 3 }, borderRadius: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                        قوانین استرداد
                    </Typography>
                    {busItem?.refundRules?.map((rule, index) => (
                        <Typography
                            component="li"
                            key={`${rule}-${index}`}
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                mb: 0.5,
                                fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" },
                            }}
                        >
                            {rule}
                        </Typography>
                    ))}
                </Card>
            )}

            {seatError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {seatError}
                </Alert>
            )}

            <Grid container item xs={12} justifyContent="flex-end">
                <BaseButton
                    style={{ width: isXs ? "100%" : "30%" }}
                    color="primary"
                    radius="radius-10"
                    size="large"
                    text="ادامه و پرداخت"
                    handleClick={handleSubmit(onSubmit)}
                    disabled={formState.isSubmitting}
                />
            </Grid>
        </Box>
    );
}
